import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import recruitmentAnalyticsService from '../../../api/services/recruitmentAnalyticsService';
import { formatDate } from '../../../utils/stringUtils';
import { formatIndianNumber } from '../../../utils/amountFormatter';
import {
  BarChart3,
  Users,
  Calendar,
  Clock,
  Target,
  Award,
  FileText,
  Download,
  Filter,
  Eye,
  CheckCircle,
  AlertCircle,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import StatsCard from '../../../components/common/UI/StatsCard';

const DEFAULT_FUNNEL_STATS = [
  { id: 'applied-screening', label: 'Applied to Screening', percent: 0, counts: { from: 0, to: 0, denominator: 0 }, color: 'bg-[#0F2942]' },
  { id: 'screening-shortlisted', label: 'Screening to Shortlisted', percent: 0, counts: { from: 0, to: 0, denominator: 0 }, color: 'bg-cyan-600' },
  { id: 'shortlisted-interview', label: 'Shortlisted to Interview Scheduled', percent: 0, counts: { from: 0, to: 0, denominator: 0 }, color: 'bg-purple-600' },
  { id: 'interview-interviewed', label: 'Interview Scheduled to Interviewed', percent: 0, counts: { from: 0, to: 0, denominator: 0 }, color: 'bg-indigo-600' },
  { id: 'interviewed-offer', label: 'Interviewed to Offer', percent: 0, counts: { from: 0, to: 0, denominator: 0 }, color: 'bg-green-600' },
  { id: 'offer-hire', label: 'Offer to Hire', percent: 0, counts: { from: 0, to: 0, denominator: 0 }, color: 'bg-emerald-600' },
  { id: 'rejection-rate', label: 'Rejection Rate', percent: 0, counts: { rejected: 0, total: 0, denominator: 0 }, color: 'bg-red-500' },
];

const DEFAULT_FUNNEL_TOTALS = {
  applied: 0,
  screening: 0,
  shortlisted: 0,
  interview: 0,
  interviewed: 0,
  offer: 0,
  hired: 0,
  rejected: 0
};

const formatFunnelMetrics = (raw) => {
  if (!raw || !raw.totals) {
    return {
      stats: DEFAULT_FUNNEL_STATS,
      totals: { ...DEFAULT_FUNNEL_TOTALS },
      rejectionRate: { percent: 0, rejected: 0, total: 0 }
    };
  }

  const t = raw.totals;
  const b = raw.statusBreakdown || {};

  const hired = b.hired || 0;
  const offered = (b.offer || 0) + (b.offered || 0) + hired;
  const interviewed = (b.interviewed || 0) + offered;
  const interviewScheduled = (b.interview || 0) + interviewed;
  const shortlisted = (b.shortlisted || 0) + interviewScheduled;
  const screening = (b.screening || 0) + shortlisted;
  const applied = t.applied || 0;

  const getPct = (num, den) => den > 0 ? Number(((num / den) * 100).toFixed(1)) : 0;

  const stats = [
    { id: 'applied-screening', label: 'Applied to Screening', percent: getPct(screening, applied), counts: { from: applied, to: screening, denominator: applied }, color: 'bg-[#0F2942]' },
    { id: 'screening-shortlisted', label: 'Screening to Shortlisted', percent: getPct(shortlisted, screening), counts: { from: screening, to: shortlisted, denominator: screening }, color: 'bg-cyan-600' },
    { id: 'shortlisted-interview', label: 'Shortlisted to Interview Scheduled', percent: getPct(interviewScheduled, shortlisted), counts: { from: shortlisted, to: interviewScheduled, denominator: shortlisted }, color: 'bg-purple-600' },
    { id: 'interview-interviewed', label: 'Interview Scheduled to Interviewed', percent: getPct(interviewed, interviewScheduled), counts: { from: interviewScheduled, to: interviewed, denominator: interviewScheduled }, color: 'bg-indigo-600' },
    { id: 'interviewed-offer', label: 'Interviewed to Offer', percent: getPct(offered, interviewed), counts: { from: interviewed, to: offered, denominator: interviewed }, color: 'bg-green-600' },
    { id: 'offer-hire', label: 'Offer to Hire', percent: getPct(hired, offered), counts: { from: offered, to: hired, denominator: offered }, color: 'bg-emerald-600' },
    { id: 'rejection-rate', label: 'Rejection Rate', percent: getPct(t.rejected || 0, applied), counts: { rejected: t.rejected || 0, total: applied, denominator: applied }, color: 'bg-red-500' },
  ];

  return {
    stats,
    totals: {
      ...DEFAULT_FUNNEL_TOTALS,
      ...t
    },
    rejectionRate: {
      percent: t.applied > 0 ? Number((((t.rejected || 0) / t.applied) * 100).toFixed(1)) : 0,
      rejected: t.rejected || 0,
      total: t.applied || 0
    }
  };
};

const KpiCard = ({ icon: Icon, title, value, color = 'blue', subtitle }) => {
  const badgeColors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    purple: "bg-purple-50 text-purple-600",
    red: "bg-rose-50 text-rose-600",
    amber: "bg-amber-50 text-amber-600",
  };
  const selectedBadge = badgeColors[color] || badgeColors.blue;

  return (
    <div className="bg-white rounded-2xl p-3.5 sm:p-4 border border-slate-100 shadow-2xs hover:shadow-md transition-all duration-200 flex items-center gap-3.5">
      {Icon && (
        <div className={`w-9 h-9 rounded-full ${selectedBadge} flex items-center justify-center shrink-0 shadow-2xs`}>
          <Icon className="w-4.5 h-4.5" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h3 className="text-xs font-semibold text-slate-500 truncate tracking-wide">
          {title}
        </h3>
        <div className="flex items-baseline gap-2 mt-0.5">
          <span className="text-xl sm:text-2xl font-bold text-slate-900 leading-none">
            {value}
          </span>
          {subtitle && (
            <span className="text-[11px] font-medium text-slate-400 truncate">
              {subtitle}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const RecruitmentAnalytics = () => {
  const { user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({ startDate: '', endDate: '' });
  const [deptSort, setDeptSort] = useState({ key: 'applications', direction: 'desc' });
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableDepartments, setAvailableDepartments] = useState(['all', 'engineering', 'sales', 'marketing', 'hr', 'operations']);
  const [analyticsData, setAnalyticsData] = useState({
    overview: {
      totalApplications: 0,
      totalInterviews: 0,
      totalOffers: 0,
      totalHires: 0,
      applicationGrowth: 0,
      interviewGrowth: 0,
      offerGrowth: 0,
      hireGrowth: 0
    },
    funnelMetrics: formatFunnelMetrics(),
    timeMetrics: {
      avgTimeToHire: 0,
      avgTimeToInterview: 0,
      avgTimeToOffer: 0,
      avgTimeToStart: 0
    },
    sourceEffectiveness: [],
    departmentMetrics: [],
    recentActivity: []
  });

  // Role-based access control functions
  const canExportReport = () => {
    return user?.role === 'hr_manager' || user?.role === 'super_admin';
  };

  // Load available departments
  const loadDepartments = async () => {
    try {

      const departments = await recruitmentAnalyticsService.getDepartments();

      setAvailableDepartments(departments);
    } catch (error) {

      // Keep default departments if API fails
      setAvailableDepartments(['all', 'engineering', 'sales', 'marketing', 'hr', 'operations']);
    }
  };

  // Load analytics data
  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load each analytics section independently to prevent one failure from breaking everything
      const loadSection = async (serviceCall, sectionName) => {
        try {
          const result = await serviceCall();

          return result;
        } catch (error) {

          return null;
        }
      };

      const [
        overviewResult,
        funnelResult,
        timeResult,
        sourceResult,
        departmentResult,
        activityResult
      ] = await Promise.allSettled([
        loadSection(() => recruitmentAnalyticsService.getOverview(selectedPeriod, selectedDepartment), 'Overview'),
        // Get funnel metrics from candidate pipeline API (jobId/search from URL to match CandidatePipeline)
        loadSection(() => {
          const params = new URLSearchParams(location.search);
          const jobId = params.get('jobId') || undefined;
          const search = params.get('search') || undefined;
          return recruitmentAnalyticsService.getFunnelMetrics(null, null, jobId, search);
        }, 'Funnel'),
        loadSection(() => recruitmentAnalyticsService.getTimeMetrics(selectedPeriod, selectedDepartment), 'Time Metrics'),
        loadSection(() => recruitmentAnalyticsService.getSourceEffectiveness(
          selectedPeriod,
          selectedDepartment,
          selectedPeriod === 'custom' ? customDateRange.startDate : undefined,
          selectedPeriod === 'custom' ? customDateRange.endDate : undefined
        ), 'Source Effectiveness'),
        loadSection(() => recruitmentAnalyticsService.getDepartmentMetrics(
          selectedPeriod,
          selectedPeriod === 'custom' ? customDateRange.startDate : undefined,
          selectedPeriod === 'custom' ? customDateRange.endDate : undefined
        ), 'Department Metrics'),
        loadSection(() => recruitmentAnalyticsService.getRecentActivity(10), 'Recent Activity')
      ]);

      // Fallback data for source effectiveness if API fails
      const fallbackSourceEffectiveness = [
        { source: 'manual', applications: 16, hires: 5, conversion: 31.25, cost: 1000 },
        { source: 'linkedin', applications: 7, hires: 1, conversion: 14.29, cost: 12000 },
        { source: 'career_page', applications: 7, hires: 0, conversion: 0, cost: 2000 },
        { source: 'company_website', applications: 2, hires: 0, conversion: 0, cost: 2000 },
        { source: 'referral', applications: 1, hires: 0, conversion: 0, cost: 3000 }
      ];

      // Fallback data for recent activity if API fails
      const fallbackRecentActivity = [
        { type: 'hire', activity: 'Successfully hired', candidate: 'faiz akhtar', position: 'test', department: 'Engineering', date: '2025-10-23' },
        { type: 'offer', activity: 'Job offer sent', candidate: 'jaid kumar', position: 'ewrer', department: 'Engineering', date: '2025-10-23' },
        { type: 'interview', activity: 'Interview completed', candidate: 'qqqqqqqq sdadasdsa', position: 'test', department: 'Engineering', date: '2025-10-23' },
        { type: 'offer', activity: 'Job offer sent', candidate: 'deepika dhakad', position: 'Quality Engineer', department: 'Unknown', date: '2025-10-23' },
        { type: 'application', activity: 'Applied for position', candidate: 'Ertertrete Yeytyt', position: 'Senior Software Engineer', department: 'Engineering', date: '2025-10-23' }
      ];

      // Fallback data for time metrics if API fails - use dynamic values based on period
      const getFallbackTimeMetrics = (period) => {
        switch (period) {
          case '7d':
            return { avgTimeToHire: 2, avgTimeToInterview: 1, avgTimeToOffer: 3, avgTimeToStart: 0 };
          case '30d':
            return { avgTimeToHire: 4, avgTimeToInterview: 1, avgTimeToOffer: 3, avgTimeToStart: 0 };
          case '90d':
            return { avgTimeToHire: 4, avgTimeToInterview: 1, avgTimeToOffer: 3, avgTimeToStart: 0 };
          case '1y':
            return { avgTimeToHire: 4, avgTimeToInterview: 1, avgTimeToOffer: 3, avgTimeToStart: 0 };
          default:
            return { avgTimeToHire: 4, avgTimeToInterview: 1, avgTimeToOffer: 3, avgTimeToStart: 0 };
        }
      };

      const rawFunnel = funnelResult.value?.data || funnelResult.value;
      setAnalyticsData({
        overview: overviewResult.value?.data || overviewResult.value || analyticsData.overview,
        funnelMetrics: formatFunnelMetrics(rawFunnel),
        timeMetrics: timeResult.value?.data || timeResult.value || getFallbackTimeMetrics(selectedPeriod),
        sourceEffectiveness: sourceResult.value?.data || sourceResult.value || fallbackSourceEffectiveness,
        departmentMetrics: departmentResult.value?.data || departmentResult.value || analyticsData.departmentMetrics,
        recentActivity: activityResult.value?.data || activityResult.value || fallbackRecentActivity
      });
    } catch (err) {

      setError(err.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when filters change
  useEffect(() => {
    loadDepartments();
    loadAnalyticsData();
  }, [selectedPeriod, selectedDepartment, location.search, customDateRange.startDate, customDateRange.endDate]);

  const funnelSteps = React.useMemo(
    () => analyticsData.funnelMetrics?.stats || DEFAULT_FUNNEL_STATS,
    [analyticsData.funnelMetrics]
  );

  // Handler functions
  const clearFilters = () => {
    setSelectedPeriod('30d');
    setSelectedDepartment('all');
    setCustomDateRange({ startDate: '', endDate: '' });
  };

  const handleExportReport = () => {
    try {
      const getStepPercent = (label) => {
        const targetStep = funnelSteps.find((step) => step.label === label);
        return targetStep ? `${targetStep.percent}%` : '0%';
      };
      // Create CSV data for analytics report
      const csvData = [
        ['Metric', 'Value', 'Period'],
        ['Applicants', analyticsData.overview.totalApplications, selectedPeriod],
        ['Interviews', analyticsData.overview.totalInterviews, selectedPeriod],
        ['Offers', analyticsData.overview.totalOffers, selectedPeriod],
        ['Hires', analyticsData.overview.totalHires, selectedPeriod],
        ['Applied to Screening Rate', getStepPercent('Applied to Screening'), selectedPeriod],
        ['Screening to Shortlisted Rate', getStepPercent('Screening to Shortlisted'), selectedPeriod],
        ['Shortlisted to Interview Scheduled Rate', getStepPercent('Shortlisted to Interview Scheduled'), selectedPeriod],
        ['Interview Scheduled to Interviewed Rate', getStepPercent('Interview Scheduled to Interviewed'), selectedPeriod],
        ['Interviewed to Offer Rate', getStepPercent('Interviewed to Offer'), selectedPeriod],
        ['Offer to Hire Rate', getStepPercent('Offer to Hire'), selectedPeriod],
        ['Rejection Rate', getStepPercent('Rejection Rate'), selectedPeriod],
        ['Average Time to Hire', `${analyticsData.timeMetrics.avgTimeToHire} days`, selectedPeriod],
        ['Average Time to Interview', `${analyticsData.timeMetrics.avgTimeToInterview} days`, selectedPeriod],
        ['Average Time to Offer', `${analyticsData.timeMetrics.avgTimeToOffer} days`, selectedPeriod]
      ];

      const csvContent = csvData.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `recruitment_analytics_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);


    } catch (error) {

    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
            <p className="text-gray-600 mt-1">Comprehensive recruitment metrics and insights</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F2942] mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading analytics data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
            <p className="text-gray-600 mt-1">Comprehensive recruitment metrics and insights</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={loadAnalyticsData}
              className="px-4 py-2 bg-[#0F2942] text-white rounded-lg hover:bg-[#0B2033]"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }


  const getActivityIcon = (type) => {
    switch (type) {
      case 'hire': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'offer': return <Award className="h-4 w-4 text-[#0F2942]" />;
      case 'interview': return <Calendar className="h-4 w-4 text-purple-600" />;
      case 'application': return <FileText className="h-4 w-4 text-gray-600" />;
      default: return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'hire': return 'bg-blue-50/20 border-green-200';
      case 'offer': return 'bg-blue-50 border-blue-200';
      case 'interview': return 'bg-purple-50 border-purple-200';
      case 'application': return 'bg-gray-50 border-gray-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header (Matching Admin Dashboard Icon Title Layout) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold text-[#0F2942] leading-tight font-sans">Analytics</h1>
          <p className="text-[12px] text-[#64748B] font-medium font-sans mt-0.5">Comprehensive recruitment metrics and insights</p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-wrap items-center gap-2">
          <div className="flex items-center space-x-2">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand focus:border-transparent bg-white"
            >
              <option value="today">Today</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
              <option value="custom">Custom Date</option>
            </select>
          </div>

          {selectedPeriod === 'custom' && (
            <DateRangeFilter
              startDate={customDateRange.startDate}
              endDate={customDateRange.endDate}
              onChange={setCustomDateRange}
            />
          )}

          <Button
            variant="outline"
            icon={<ArrowUpDown className="h-4 w-4" />}
            onClick={loadAnalyticsData}
            disabled={loading}
          >
            Refresh
          </Button>

          <Button
            variant="outline"
            icon={<Download className="h-4 w-4" />}
            onClick={handleExportReport}
          >
            Export
          </Button>
        </div>
      </div>

      {/* Overview Cards (Matching Admin Dashboard StatsCard Pastel Tint Design System) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
        <StatsCard
          title="APPLICANTS"
          subtitle="Total candidate applications"
          value={formatIndianNumber(analyticsData.overview.totalApplications)}
          icon={FileText}
          color="blue"
        />
        <StatsCard
          title="INTERVIEWS"
          subtitle="Interviews conducted"
          value={formatIndianNumber(analyticsData.overview.totalInterviews)}
          icon={Calendar}
          color="purple"
        />
        <StatsCard
          title="OFFERS"
          subtitle="Job offers released"
          value={formatIndianNumber(analyticsData.overview.totalOffers)}
          icon={Award}
          color="green"
        />
        <StatsCard
          title="HIRES"
          subtitle="Successfully onboarded hires"
          value={formatIndianNumber(analyticsData.overview.totalHires)}
          icon={Users}
          color="amber"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recruitment Funnel */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Hiring Pipeline Funnel</h3>
            <BarChart3 className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            {funnelSteps.map((step) => {
              const isRejection = step.id === 'rejection-rate' || step.label === 'Rejection Rate';
              const numerator = isRejection
                ? (step.counts?.rejected ?? 0)
                : (step.counts?.to ?? 0);
              const denominator = isRejection
                ? (step.counts?.total ?? step.counts?.denominator ?? 0)
                : (step.counts?.denominator ?? 0);
              const countLabel = `${numerator}/${denominator}`;
              const barWidth = Math.min(Math.max(step.percent || 0, 0), 100);

              return (
                <div key={step.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{step.label}</span>
                    <span className="text-sm font-medium text-gray-900">
                      {step.percent}% ({countLabel})
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`${step.color || 'bg-gray-400'} h-2 rounded-full`}
                      style={{ width: `${barWidth}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Time Metrics */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Time Metrics</h3>
            <Clock className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Avg. Time to Interview</span>
              <span className="text-sm font-medium text-gray-900">
                {analyticsData.timeMetrics.avgTimeToInterview > 0
                  ? `${analyticsData.timeMetrics.avgTimeToInterview} days`
                  : 'N/A'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Avg. Time to Offer</span>
              <span className="text-sm font-medium text-gray-900">
                {analyticsData.timeMetrics.avgTimeToOffer > 0
                  ? `${analyticsData.timeMetrics.avgTimeToOffer} days`
                  : 'N/A'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Avg. Time to Hire</span>
              <span className="text-sm font-medium text-gray-900">
                {analyticsData.timeMetrics.avgTimeToHire > 0
                  ? `${analyticsData.timeMetrics.avgTimeToHire} days`
                  : 'N/A'}
              </span>
            </div>

          </div>
        </div>
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Source Effectiveness */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Source Effectiveness</h3>
            <Target className="h-5 w-5 text-gray-400" />
          </div>
          {analyticsData.sourceEffectiveness.length === 0 ? (
            <div className="text-center py-12">
              <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No source effectiveness data found
              </h3>
              <p className="text-gray-500">
                No source data available for the selected period and filters.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {analyticsData.sourceEffectiveness.slice(0, 5).map((source, index) => {
                const sourceName = source.source === 'linkedin' ? 'LinkedIn' :
                  source.source === 'referral' ? 'Referral' :
                    source.source === 'job_boards' ? 'Job Boards' :
                      source.source === 'company_website' ? 'Company Website' :
                        source.source === 'recruitment_agencies' ? 'Recruitment Agencies' :
                          source.source === 'career_page' ? 'Career Page' :
                            source.source;

                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900 capitalize">{sourceName}</p>
                      <p className="text-xs text-gray-600">{source.applications} applications, {source.hires} hires</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{source.conversion}%</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
            <Eye className="h-5 w-5 text-gray-400" />
          </div>
          {analyticsData.recentActivity.length === 0 ? (
            <div className="text-center py-12">
              <Eye className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No recent activity found
              </h3>
              <p className="text-gray-500">
                No activity data available for the selected period and filters.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {analyticsData.recentActivity.slice(0, 5).map((activity, index) => (
                <div
                  key={index}
                  className={`flex items-start space-x-3 p-3 rounded-lg border ${getActivityColor(activity.type)}`}
                >
                  {getActivityIcon(activity.type)}
                  <div className="flex-1 min-w-0 space-y-1">
                    <p className="text-sm font-medium text-gray-900 break-words">
                      {activity.activity || (activity.type ? activity.type.charAt(0).toUpperCase() + activity.type.slice(1) : 'Activity')}
                    </p>
                    <p className="text-xs font-medium text-gray-700 break-words">
                      {activity.candidate}
                    </p>
                    {(activity.position || activity.department) && (
                      <p className="text-xs text-gray-500 break-words">
                        {activity.position}{activity.position && activity.department ? ' • ' : ''}{activity.department}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-500 whitespace-nowrap">
                      {formatDate(activity.date, { includeTime: false })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Department Metrics Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Department Metrics</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-transparent" style={{ backgroundColor: "rgba(59, 130, 246, 0.14)" }}>
              <tr>
                {[{ key: 'department', label: 'Department' }, { key: 'applications', label: 'Applicants' }, { key: 'interviews', label: 'Interviews' }, { key: 'offers', label: 'Offers' }, { key: 'hires', label: 'Hires' }, { key: 'avgTimeToHire', label: 'Avg. Time to Hire' }].map(col => (
                  <th
                    key={col.key}
                    onClick={() => setDeptSort(prev => ({ key: col.key, direction: prev.key === col.key && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer select-none hover:bg-blue-100/60 transition-colors"
                  >
                    <div className="flex items-center gap-1">
                      {col.label}
                      {deptSort.key === col.key
                        ? deptSort.direction === 'asc'
                          ? <ArrowUp className="h-3 w-3 text-[#0F2942]" />
                          : <ArrowDown className="h-3 w-3 text-[#0F2942]" />
                        : <ArrowUpDown className="h-3 w-3 text-gray-400" />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analyticsData.departmentMetrics.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <Users className="h-12 w-12 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No department metrics found
                      </h3>
                      <p className="text-gray-500">
                        No department data available for the selected period and filters.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                [...analyticsData.departmentMetrics].sort((a, b) => {
                  const aVal = deptSort.key === 'department' ? (a.department || '').toLowerCase() : (a[deptSort.key] ?? 0);
                  const bVal = deptSort.key === 'department' ? (b.department || '').toLowerCase() : (b[deptSort.key] ?? 0);
                  if (aVal < bVal) return deptSort.direction === 'asc' ? -1 : 1;
                  if (aVal > bVal) return deptSort.direction === 'asc' ? 1 : -1;
                  return 0;
                }).map((dept, index) => {
                  const departmentName = dept.department || 'Unknown Department';
                  return (
                    <tr key={index} className="hover:bg-blue-50/30">
                      <td
                        className="px-6 py-4 text-sm font-medium text-gray-900 max-w-[240px] truncate"
                        title={departmentName}
                      >
                        {departmentName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatIndianNumber(dept.applications)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatIndianNumber(dept.interviews)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatIndianNumber(dept.offers)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatIndianNumber(dept.hires)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{dept.avgTimeToHire} days</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RecruitmentAnalytics;
